import { useState, useEffect, useCallback, useMemo } from 'react';
import { dashboardService, type DashboardCountsResponse } from '../services/dashboard.service';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { useAppNavigate } from '../hooks/useAppNavigate';
import ScreenHeader from '../components/common/screen-header';

// Human labels + display order for the category sections.
const CATEGORY_META: { key: string; label: string }[] = [
  { key: 'organization', label: 'Organization' },
  { key: 'masters', label: 'Masters' },
  { key: 'configurations', label: 'Configurations' },
  { key: 'monitoring', label: 'Monitoring' },
];

// Convert camelCase/PascalCase to readable format
const formatName = (name: string): string => {
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

// Menu metadata - icons, colors, and categories
const MENU_METADATA: Record<string, { icon: string; bgColor: string; category: string }> = {
  // Organization
  units: { icon: 'pi pi-building', bgColor: 'bg-blue-600', category: 'organization' },
  personnel: { icon: 'pi pi-users', bgColor: 'bg-green-600', category: 'organization' },
  departments: { icon: 'pi pi-sitemap', bgColor: 'bg-cyan-600', category: 'organization' },
  assignment: { icon: 'pi pi-sitemap', bgColor: 'bg-purple-600', category: 'organization' },
  post: { icon: 'pi pi-id-card', bgColor: 'bg-cyan-500', category: 'organization' },

  // Masters
  district: { icon: 'pi pi-globe', bgColor: 'bg-emerald-600', category: 'masters' },
  mandals: { icon: 'pi pi-map', bgColor: 'bg-lime-600', category: 'masters' },
  rank: { icon: 'pi pi-star', bgColor: 'bg-yellow-600', category: 'masters' },
  unittype: { icon: 'pi pi-tags', bgColor: 'bg-pink-600', category: 'masters' },
  levels: { icon: 'pi pi-sitemap', bgColor: 'bg-indigo-500', category: 'masters' },
  unitvillages: { icon: 'pi pi-map-marker', bgColor: 'bg-purple-600', category: 'masters' },
  designationmaster: { icon: 'pi pi-id-card', bgColor: 'bg-amber-600', category: 'masters' },
  errormaster: { icon: 'pi pi-exclamation-triangle', bgColor: 'bg-red-600', category: 'masters' },
  feedbackmaster: { icon: 'pi pi-comment', bgColor: 'bg-teal-500', category: 'masters' },
  logmaster: { icon: 'pi pi-file-edit', bgColor: 'bg-rose-600', category: 'masters' },
  notificationmaster: { icon: 'pi pi-bell', bgColor: 'bg-teal-600', category: 'masters' },
  prompttable: { icon: 'pi pi-comments', bgColor: 'bg-indigo-600', category: 'masters' },

  // Configurations
  modulejobmappings: { icon: 'pi pi-link', bgColor: 'bg-indigo-600', category: 'configurations' },
  permissionmappings: { icon: 'pi pi-key', bgColor: 'bg-cyan-600', category: 'configurations' },
  roles: { icon: 'pi pi-shield', bgColor: 'bg-amber-600', category: 'configurations' },
  systemroles: { icon: 'pi pi-verified', bgColor: 'bg-indigo-600', category: 'configurations' },
  postrolemappings: { icon: 'pi pi-link', bgColor: 'bg-purple-600', category: 'configurations' },
  userrolepermissions: { icon: 'pi pi-id-card', bgColor: 'bg-pink-600', category: 'configurations' },
  valuesets: { icon: 'pi pi-cog', bgColor: 'bg-orange-600', category: 'configurations' },
  jobs: { icon: 'pi pi-briefcase', bgColor: 'bg-sky-600', category: 'configurations' },
  modules: { icon: 'pi pi-box', bgColor: 'bg-violet-600', category: 'configurations' },
  permissions: { icon: 'pi pi-lock', bgColor: 'bg-emerald-600', category: 'configurations' },

  // Monitoring
  errorlogs: { icon: 'pi pi-exclamation-circle', bgColor: 'bg-red-500', category: 'monitoring' },
  feedbacks: { icon: 'pi pi-comments', bgColor: 'bg-blue-400', category: 'monitoring' },
  logtransaction: { icon: 'pi pi-history', bgColor: 'bg-slate-600', category: 'monitoring' },
  notifications: { icon: 'pi pi-bell', bgColor: 'bg-yellow-500', category: 'monitoring' },
  promptexecutions: { icon: 'pi pi-play-circle', bgColor: 'bg-purple-500', category: 'monitoring' },

  // Other
  fileupload: { icon: 'pi pi-upload', bgColor: 'bg-gray-500', category: 'masters' },
  testmaster: { icon: 'pi pi-wrench', bgColor: 'bg-gray-500', category: 'masters' },
};

const normalizeName = (name: string): string => name.toLowerCase().replace(/[-_\s]/g, '');

const getMetadata = (name: string) => {
  const normalized = normalizeName(name);
  return MENU_METADATA[normalized] || { icon: 'pi pi-box', bgColor: 'bg-gray-600', category: 'masters' };
};

interface DashboardItem {
  name: string;
  displayName: string;
  count: number;
  route: string;
  icon: string;
  bgColor: string;
  category: string;
}

const Dashboard = () => {
  const navigate = useAppNavigate();
  const [counts, setCounts] = useState<DashboardCountsResponse>({ platform: {}, application: {} });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchCounts = useCallback(async () => {
    try {
      const data = await dashboardService.getCounts();
      setCounts(data);
    } catch (error) {
      console.error('Failed to fetch dashboard counts:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchCounts();
  };

  // Process and categorize items
  const processedItems = useMemo(() => {
    const platformItems: DashboardItem[] = Object.entries(counts.platform)
      .filter(([, item]) => item.isMenu === true && item.route)
      .map(([name, item]) => {
        const meta = getMetadata(name);
        return {
          name,
          displayName: item.displayName?.replace(/_/g, ' ') || formatName(name),
          count: item.count,
          route: `/${item.route}`,
          icon: meta.icon,
          bgColor: meta.bgColor,
          category: meta.category,
        };
      });

    const applicationItems: DashboardItem[] = Object.entries(counts.application)
      .filter(([, item]) => item.isMenu === true && item.route)
      .map(([name, item]) => {
        const meta = getMetadata(name);
        return {
          name,
          displayName: item.displayName?.replace(/_/g, ' ') || formatName(name),
          count: item.count,
          route: `/${item.route}`,
          icon: meta.icon,
          bgColor: meta.bgColor,
          category: meta.category,
        };
      });

    return [...platformItems, ...applicationItems];
  }, [counts]);

  // Helper to find count from raw API data by key name (case-insensitive)
  const getCountFromRawData = useCallback((searchKey: string): number => {
    const normalizedSearch = searchKey.toLowerCase();

    // Search in platform data
    for (const [key, item] of Object.entries(counts.platform)) {
      if (key.toLowerCase().includes(normalizedSearch)) {
        return item.count;
      }
    }

    // Search in application data
    for (const [key, item] of Object.entries(counts.application)) {
      if (key.toLowerCase().includes(normalizedSearch)) {
        return item.count;
      }
    }

    return 0;
  }, [counts]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const personnelCount = getCountFromRawData('personnel');
    const unitsCount = getCountFromRawData('units');
    const totalRecords = processedItems.reduce((sum, it) => sum + (it.count || 0), 0);
    return { totalRecords, personnelCount, unitsCount, collections: processedItems.length };
  }, [processedItems, getCountFromRawData]);

  // Group the per-collection cards by category, in a stable display order.
  const groupedItems = useMemo(() => {
    const byCat = new Map<string, DashboardItem[]>();
    for (const it of processedItems) {
      const list = byCat.get(it.category) || [];
      list.push(it);
      byCat.set(it.category, list);
    }
    const ordered: { key: string; label: string; items: DashboardItem[] }[] = [];
    for (const { key, label } of CATEGORY_META) {
      const items = (byCat.get(key) || []).sort((a, b) => a.displayName.localeCompare(b.displayName));
      if (items.length) ordered.push({ key, label, items });
      byCat.delete(key);
    }
    // Any remaining categories not in CATEGORY_META.
    for (const [key, items] of byCat) {
      ordered.push({ key, label: formatName(key), items: items.sort((a, b) => a.displayName.localeCompare(b.displayName)) });
    }
    return ordered;
  }, [processedItems]);

  // Render summary stat card
  const renderStatCard = (
    title: string,
    value: number | string,
    icon: string,
    gradient: string,
    subtitle?: string
  ) => (
    <div className="ds-card p-5 hover:shadow-lg transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-label font-medium text-ink-muted">{title}</p>
          <p className="text-3xl font-bold tracking-tight text-ink mt-1">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          {subtitle && <p className="text-caption text-ink-muted mt-1">{subtitle}</p>}
        </div>
        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg`}>
          <i className={`${icon} text-white text-xl`} />
        </div>
      </div>
    </div>
  );

  // Render skeleton loading
  const renderSkeleton = () => (
    <div className="animate-pulse">
      {/* Summary Stats Skeleton - 3 cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="ds-card p-5">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20" />
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-24" />
              </div>
              <div className="w-14 h-14 rounded-xl bg-gray-200 dark:bg-gray-700" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="p-4 md:p-6 min-h-screen bg-canvas dark:bg-gray-900" data-testid="SCR-Dashboard-Main">
      <div className="max-w-7xl mx-auto">
        {/* Header — uses the default brand-gradient icon tile */}
        <ScreenHeader
          icon="pi pi-chart-bar"
          title="Analytics Dashboard"
          subtitle="4sCoreASTRUM Master Data Overview"
          right={<RefreshButton onClick={handleRefresh} loading={refreshing} />}
        />

        {/* Loading State */}
        {loading && renderSkeleton()}

        {/* Dashboard Content */}
        {!loading && processedItems.length > 0 && (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {renderStatCard(
                'Total Records',
                summaryStats.totalRecords,
                'pi pi-database',
                'from-indigo-500 to-indigo-700',
                `Across ${summaryStats.collections} collections`
              )}
              {renderStatCard(
                'Personnel',
                summaryStats.personnelCount,
                'pi pi-users',
                'from-violet-500 to-violet-700',
                'Total personnel records'
              )}
              {renderStatCard(
                'Units',
                summaryStats.unitsCount,
                'pi pi-building',
                'from-fuchsia-500 to-fuchsia-700',
                'Organizational units'
              )}
              {renderStatCard(
                'Collections',
                summaryStats.collections,
                'pi pi-th-large',
                'from-indigo-400 to-indigo-600',
                'Data sets available'
              )}
            </div>

            {/* Per-collection stat cards, grouped by category */}
            {groupedItems.map((group) => (
              <section key={group.key} className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <h2 className="text-heading font-semibold text-ink">
                    {group.label}
                  </h2>
                  <span className="text-caption text-ink-muted">({group.items.length})</span>
                  <div className="flex-1 h-px bg-line" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                  {group.items.map((item) => (
                    <button
                      key={item.name}
                      type="button"
                      onClick={() => navigate(item.route)}
                      className="ds-card group text-left p-4 hover:shadow-md hover:-translate-y-0.5 hover:border-gray-300 dark:hover:border-gray-600 transition-all"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className={`w-9 h-9 rounded-lg ${item.bgColor} flex items-center justify-center shadow-sm`}>
                          <i className={`${item.icon} text-white text-sm`} />
                        </div>
                        <i className="pi pi-arrow-up-right text-xs text-gray-300 dark:text-gray-600 group-hover:text-gray-500 transition-colors" />
                      </div>
                      <p className="text-2xl font-bold tracking-tight text-ink leading-none">
                        {item.count.toLocaleString()}
                      </p>
                      <p className="text-label font-medium text-ink-muted mt-1 capitalize truncate">
                        {item.displayName}
                      </p>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </>
        )}

        {/* Empty State */}
        {!loading && processedItems.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
              <i className="pi pi-inbox text-gray-400 text-3xl" />
            </div>
            <h3 className="text-lg font-medium text-ink mb-2">No Data Available</h3>
            <p className="text-sm text-ink-muted">
              Dashboard data is not available at the moment. Please try refreshing.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
